/**
 * AI Setup Assistant API service
 */
import axios, { AxiosError } from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

// --- Types ---
export interface IntentProposalItem {
  key: string;
  display_name: string;
  description: string;
}

export interface StartResponse {
  session_id: number;
  step: string;
  created_at?: string;
}

export interface AnalyzeResponse {
  session_id: number;
  step: string;
  company_profile?: Record<string, unknown>;
  questions?: string[] | null;
}

export interface Step2Answer {
  question_index: number;
  answer?: string | null;
  skipped: boolean;
}

export interface SubmitStep2Response {
  session_id: number;
  step: string;
  intents: IntentProposalItem[];
}

export interface Step4Answer {
  question_index: number;
  answer?: string | null;
  skipped?: boolean;
  in_knowledge_base?: boolean;
}

export interface SubmitStep4Response {
  session_id: number;
  step: string;
}

export interface ProposeIntentsResponse {
  session_id: number;
  step: string;
  intents: IntentProposalItem[];
}

export interface ConfirmIntentsResponse {
  session_id: number;
  step: string;
  intents: IntentProposalItem[];
}

export interface IntentDataResponse {
  session_id: number;
  step: string;
  questions: string[] | null;
  done: boolean;
}

export interface GenerateResponse {
  session_id: number;
  step: string;
  batch_id: string;
}

export interface GenerationStatusResponse {
  batch_id: string;
  project_id: number;
  steps_id: number;
  expected: number;
  completed: number;
  failed: number;
  done: boolean;
  all_success: boolean;
}

export interface SessionResponse {
  id: number;
  project_id: number;
  step: string;
  chat_history: Array<{ role: string; content: string }>;
  collected_data: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface ActiveSessionResponse {
  active: boolean;
  session_id: number | null;
  step?: string;
}

export interface StepConflictDetail {
  code: string;
  expected_step?: string;
  actual_step?: string;
  message: string;
}

export function isStepConflictError(err: unknown): err is AxiosError<{ detail: StepConflictDetail }> {
  return axios.isAxiosError(err) && err.response?.status === 409;
}

// --- API ---

export const aiSetupStart = async (
  projectId: number,
  forceRestart = false
): Promise<StartResponse> => {
  const { data } = await axios.post<StartResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/start`,
    { force_restart: forceRestart },
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupAnalyze = async (
  projectId: number,
  rawText: string,
  sessionId?: number
): Promise<AnalyzeResponse> => {
  const { data } = await axios.post<AnalyzeResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/analyze`,
    { raw_text: rawText, session_id: sessionId ?? undefined },
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupSubmitStep2 = async (
  projectId: number,
  answers: Step2Answer[],
  sessionId?: number
): Promise<SubmitStep2Response> => {
  const { data } = await axios.post<SubmitStep2Response>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/step2/submit`,
    { answers, session_id: sessionId ?? undefined },
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupProposeIntents = async (
  projectId: number,
  sessionId?: number
): Promise<ProposeIntentsResponse> => {
  const params = sessionId != null ? { session_id: sessionId } : undefined;
  const { data } = await axios.post<ProposeIntentsResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/propose-intents`,
    {},
    { headers: getAuthHeaders(), params }
  );
  return data;
};

export const aiSetupConfirmIntents = async (
  projectId: number,
  intents: IntentProposalItem[],
  sessionId?: number
): Promise<ConfirmIntentsResponse> => {
  const { data } = await axios.post<ConfirmIntentsResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/confirm-intents`,
    {
      intents: intents.map((i) => ({
        key: i.key,
        display_name: i.display_name,
        description: i.description,
      })),
      session_id: sessionId ?? undefined,
    },
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupIntentDataStart = async (
  projectId: number,
  sessionId?: number
): Promise<IntentDataResponse> => {
  const params = sessionId != null ? { session_id: sessionId } : undefined;
  const { data } = await axios.post<IntentDataResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/intent-data/start`,
    {},
    { headers: getAuthHeaders(), params }
  );
  return data;
};

export const aiSetupSubmitStep4 = async (
  projectId: number,
  answers: Step4Answer[],
  sessionId?: number
): Promise<SubmitStep4Response> => {
  const { data } = await axios.post<SubmitStep4Response>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/intent-data/submit`,
    { answers, session_id: sessionId ?? undefined },
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupStructureContext = async (
  projectId: number,
  sessionId?: number
): Promise<{ session_id: number; step: string; structured_context_preview?: string }> => {
  const params = sessionId != null ? { session_id: sessionId } : undefined;
  const { data } = await axios.post(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/structure-context`,
    {},
    { headers: getAuthHeaders(), params }
  );
  return data;
};

export const aiSetupGenerate = async (
  projectId: number,
  sessionId?: number
): Promise<GenerateResponse> => {
  const params = sessionId != null ? { session_id: sessionId } : undefined;
  const { data } = await axios.post<GenerateResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/generate`,
    {},
    { headers: getAuthHeaders(), params }
  );
  return data;
};

export const aiSetupGetGenerationStatus = async (
  projectId: number,
  batchId: string
): Promise<GenerationStatusResponse> => {
  const { data } = await axios.get<GenerationStatusResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/generation-status/${batchId}`,
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupGetSession = async (
  projectId: number,
  stepsId: number
): Promise<SessionResponse> => {
  const { data } = await axios.get<SessionResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/session/${stepsId}`,
    { headers: getAuthHeaders() }
  );
  return data;
};

export const aiSetupGetActive = async (
  projectId: number
): Promise<ActiveSessionResponse> => {
  const { data } = await axios.get<ActiveSessionResponse>(
    `${API_URL}/api/v1/projects/${projectId}/ai-setup/active`,
    { headers: getAuthHeaders() }
  );
  return data;
};
